import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth.jsx";
import { useI18n } from "@/lib/i18n";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { ClipboardCheck, GraduationCap, CheckCheck, Clock3, XCircle, CircleDashed } from "lucide-react";
import StaffCheckInCard from "@/components/app/StaffCheckInCard";
import { PageHeader } from "@/components/app/PageHeader";

// -------- Staff Home --------
export function StaffHome() {
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const [summary, setSummary] = useState({});
  useEffect(() => { api.get("/dashboard/summary").then((r) => setSummary(r.data)).catch(() => {}); }, []);
  return (
    <div className="space-y-6" data-testid="staff-dashboard">
      <PageHeader
        eyebrow="Kelas Program Membaca PMM"
        title={`${t("hello")}, ${user?.name.split(" ")[0]}`}
        subtitle={t("staff_dashboard_title")}
      />
      <div className="grid gap-6 md:grid-cols-2">
        <StaffCheckInCard />
        <Card className="pmm-pop rounded-[1.5rem] border-border/60 shadow-md transition-all duration-300 hover:-translate-y-1 hover:shadow-xl" style={{ animationDelay: "0.12s" }}><CardContent className="p-6">
          <h2 className="font-display text-sm font-extrabold uppercase tracking-widest text-blue-950">{lang === "ms" ? "Hari ini di pusat" : "Today at the center"}</h2>
          <div className="mt-5 grid grid-cols-2 gap-4">
            <div className="flex items-center gap-3">
              <div className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-gradient-to-br from-orange-600 to-orange-400 text-white shadow-lg shadow-orange-500/30">
                <ClipboardCheck className="h-6 w-6" />
              </div>
              <div>
                <div className="text-xs font-bold uppercase tracking-widest text-muted-foreground">{t("students")}</div>
                <div className="num font-display text-3xl font-extrabold tracking-tight text-blue-950">{summary.students_today ?? 0}</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-gradient-to-br from-blue-900 to-blue-600 text-white shadow-lg shadow-blue-800/30">
                <GraduationCap className="h-6 w-6" />
              </div>
              <div>
                <div className="text-xs font-bold uppercase tracking-widest text-muted-foreground">{lang === "ms" ? "Jumlah" : "Total"}</div>
                <div className="num font-display text-3xl font-extrabold tracking-tight text-blue-950">{summary.total_students ?? 0}</div>
              </div>
            </div>
          </div>
        </CardContent></Card>
      </div>
    </div>
  );
}

// -------- Teacher Home --------
export function TeacherHome() {
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const [summary, setSummary] = useState({});
  useEffect(() => { api.get("/dashboard/summary").then((r) => setSummary(r.data)).catch(() => {}); }, []);
  return (
    <div className="space-y-6" data-testid="teacher-dashboard">
      <PageHeader
        eyebrow="Kelas Program Membaca PMM"
        title={`${t("hello")}, ${user?.name.split(" ")[0]}`}
        subtitle={t("teacher_dashboard_title")}
      />
      <div className="grid gap-6 md:grid-cols-2">
        <StaffCheckInCard />
        <Card className="pmm-pop rounded-[1.5rem] border-border/60 shadow-md transition-all duration-300 hover:-translate-y-1 hover:shadow-xl" style={{ animationDelay: "0.12s" }}><CardContent className="p-6">
          <h2 className="font-display text-sm font-extrabold uppercase tracking-widest text-blue-950">{lang === "ms" ? "Ditanda hari ini" : "Marked today"}</h2>
          <div className="mt-4 flex items-center gap-4">
            <div className="grid h-14 w-14 shrink-0 place-items-center rounded-2xl bg-gradient-to-br from-yellow-500 to-orange-500 text-white shadow-lg shadow-yellow-500/30">
              <ClipboardCheck className="h-7 w-7" />
            </div>
            <div className="num font-display text-5xl font-extrabold tracking-tight text-blue-950">{summary.attendance_marked_today ?? 0}</div>
          </div>
          <p className="mt-3 text-sm font-semibold text-muted-foreground">{lang === "ms" ? "Pergi ke Kehadiran untuk tanda lagi." : "Head to Attendance to tick more."}</p>
        </CardContent></Card>
      </div>
    </div>
  );
}

// -------- Student attendance page (staff+teacher) --------
export function StudentAttendancePage() {
  const { t, lang } = useI18n();
  const [students, setStudents] = useState([]);
  const [records, setRecords] = useState([]);
  const [status, setStatus] = useState({});
  const [busyId, setBusyId] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);

  const loadAll = () => {
    api.get("/students").then((r) => setStudents(r.data.students));
    api.get("/attendance/student").then((r) => setRecords(r.data.records));
  };
  useEffect(() => { loadAll(); }, []);

  const mark = async (student_id, statusVal) => {
    setBusyId(student_id);
    try {
      await api.post("/attendance/student/manual", {
        student_id,
        status: statusVal,
      });
      toast.success("Marked");
      loadAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusyId(null);
    }
  };

  const markAll = async () => {
    if (!window.confirm(lang === "ms" ? "Tanda SEMUA murid sebagai hadir untuk hari ini?" : "Mark ALL students present for today?")) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post("/attendance/student/manual-bulk");
      if (data.marked === 0) {
        toast.info(lang === "ms" ? "Semua murid sudah ditanda hari ini" : "All students already marked today");
      } else {
        toast.success(lang === "ms" ? `${data.marked} murid ditanda hadir${data.skipped ? ` · ${data.skipped} sudah ditanda` : ""}` : `${data.marked} marked present${data.skipped ? ` · ${data.skipped} already marked` : ""}`);
      }
      loadAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBulkBusy(false);
    }
  };

  const today = new Date().toISOString().slice(0, 10);

  const latestByStudent = {};
  records.filter((r) => r.date === today).forEach((r) => {
    if (!latestByStudent[r.student_id]) latestByStudent[r.student_id] = r;
  });
  const counts = { present: 0, late: 0, absent: 0 };
  Object.values(latestByStudent).forEach((r) => {
    if (counts[r.status] !== undefined) counts[r.status] += 1;
  });
  const unmarked = students.filter((s) => !latestByStudent[s.id]);
  const sortedStudents = [...unmarked, ...students.filter((s) => latestByStudent[s.id])];
  const todayChip = (s) => {
    const rec = latestByStudent[s.id];
    if (!rec) return <span className="shrink-0 rounded-full bg-orange-500/10 px-2.5 py-0.5 text-[11px] font-bold text-orange-600">{lang === "ms" ? "Belum ditanda" : "Not marked"}</span>;
    const cls = rec.status === "present" ? "bg-green-500/10 text-green-600" : rec.status === "late" ? "bg-yellow-500/15 text-yellow-600" : "bg-red-500/10 text-red-600";
    return <span className={`shrink-0 rounded-full px-2.5 py-0.5 text-[11px] font-bold ${cls}`}>{t(rec.status)}</span>;
  };

  return (
    <div className="space-y-8" data-testid="teacher-attendance">
      <PageHeader
        eyebrow={today}
        title={t("markAttendance")}
        action={
          <Button
            onClick={markAll}
            disabled={bulkBusy || students.length === 0}
            data-testid="mark-all-present-btn"
            className="gap-2 rounded-full bg-gradient-to-r from-orange-600 to-orange-500 px-6 font-display text-xs font-extrabold uppercase tracking-wide text-white shadow-lg shadow-orange-500/40 transition-transform duration-300 hover:scale-105"
          >
            <CheckCheck className="h-4 w-4" />
            {lang === "ms" ? "Tanda Semua Hadir" : "Mark All Present"}
          </Button>
        }
      />

      <div className="pmm-pop grid grid-cols-2 gap-3 md:grid-cols-4" style={{ animationDelay: "0.05s" }} data-testid="attendance-summary-bar">
        <Card className="rounded-[1.25rem] border-2 border-orange-500/30 shadow-md"><CardContent className="flex items-center gap-3 p-4">
          <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-orange-500/10 text-orange-600"><CircleDashed className="h-4 w-4" /></span>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{lang === "ms" ? "Belum" : "Unmarked"}</div>
            <div className="num font-display text-2xl font-extrabold text-orange-600" data-testid="summary-unmarked">{unmarked.length}</div>
          </div>
        </CardContent></Card>
        <Card className="rounded-[1.25rem] border-border/60 shadow-md"><CardContent className="flex items-center gap-3 p-4">
          <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-green-500/10 text-green-600"><ClipboardCheck className="h-4 w-4" /></span>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{t("present")}</div>
            <div className="num font-display text-2xl font-extrabold text-green-600" data-testid="summary-present">{counts.present}</div>
          </div>
        </CardContent></Card>
        <Card className="rounded-[1.25rem] border-border/60 shadow-md"><CardContent className="flex items-center gap-3 p-4">
          <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-yellow-500/15 text-yellow-600"><Clock3 className="h-4 w-4" /></span>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{t("late")}</div>
            <div className="num font-display text-2xl font-extrabold text-yellow-600" data-testid="summary-late">{counts.late}</div>
          </div>
        </CardContent></Card>
        <Card className="rounded-[1.25rem] border-border/60 shadow-md"><CardContent className="flex items-center gap-3 p-4">
          <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-red-500/10 text-red-600"><XCircle className="h-4 w-4" /></span>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{t("absent")}</div>
            <div className="num font-display text-2xl font-extrabold text-red-600" data-testid="summary-absent">{counts.absent}</div>
          </div>
        </CardContent></Card>
      </div>

      <Card className="pmm-pop overflow-hidden rounded-[1.5rem] border-border/60 shadow-lg" style={{ animationDelay: "0.1s" }}><CardContent className="p-0">
        <div className="divide-y divide-border/60 md:hidden" data-testid="mark-attendance-mobile-list">
          {sortedStudents.map((s) => (
            <div key={s.id} className="p-4" data-testid={`m-stu-${s.id}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0 font-display font-extrabold text-blue-950">{s.full_name}</div>
                <div className="flex shrink-0 items-center gap-1.5">
                  <span className="num rounded-full bg-blue-800/10 px-2.5 py-0.5 text-[11px] font-bold text-blue-800">{s.level}</span>
                  {todayChip(s)}
                </div>
              </div>
              <div className="mt-3 grid grid-cols-3 gap-2">
                {[
                  { v: "present", label: t("present") },
                  { v: "late", label: t("late") },
                  { v: "absent", label: t("absent") },
                ].map((o) => (
                  <Button
                    key={o.v}
                    size="sm"
                    variant={o.v === "present" ? "default" : o.v === "late" ? "secondary" : "outline"}
                    onClick={() => mark(s.id, o.v)}
                    disabled={busyId === s.id}
                    className="h-9 rounded-full text-xs"
                    data-testid={`m-mark-${o.v}-${s.id}`}
                  >
                    {o.label}
                  </Button>
                ))}
              </div>
            </div>
          ))}
          {students.length === 0 && <div className="p-6 text-center text-sm text-muted-foreground">{t("noRecordsYet")}</div>}
        </div>
        <div className="hidden md:block">
        <Table>
          <TableHeader><TableRow className="bg-blue-950 hover:bg-blue-950">
            <TableHead className="text-xs font-extrabold uppercase tracking-widest text-yellow-400">{t("fullName")}</TableHead>
            <TableHead className="text-xs font-extrabold uppercase tracking-widest text-yellow-400">{t("level")}</TableHead>
            <TableHead className="text-xs font-extrabold uppercase tracking-widest text-yellow-400">{lang === "ms" ? "Hari Ini" : "Today"}</TableHead>
            <TableHead className="w-72 text-xs font-extrabold uppercase tracking-widest text-yellow-400">Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {sortedStudents.map((s) => (
              <TableRow key={s.id} className="transition-colors hover:bg-orange-50">
                <TableCell className="font-semibold text-blue-950">{s.full_name}</TableCell>
                <TableCell><span className="num rounded-full bg-blue-800/10 px-2.5 py-0.5 text-xs font-bold text-blue-800">{s.level}</span></TableCell>
                <TableCell>{todayChip(s)}</TableCell>
                <TableCell>
                  <div className="flex gap-2">
                    {[
                      { v: "present", label: t("present") },
                      { v: "late", label: t("late") },
                      { v: "absent", label: t("absent") },
                    ].map((o) => (
                      <Button
                        key={o.v}
                        size="sm"
                        variant={o.v === "present" ? "default" : o.v === "late" ? "secondary" : "outline"}
                        onClick={() => mark(s.id, o.v)}
                        disabled={busyId === s.id}
                        className="rounded-full"
                        data-testid={`mark-${o.v}-${s.id}`}
                      >
                        {o.label}
                      </Button>
                    ))}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        </div>
      </CardContent></Card>

      <section>
        <h2 className="mb-3 font-display text-sm font-extrabold uppercase tracking-widest text-blue-950">Recent</h2>
        <Card className="pmm-pop overflow-hidden rounded-[1.5rem] border-border/60 shadow-lg" style={{ animationDelay: "0.15s" }}><CardContent className="p-0">
          <div className="divide-y divide-border/60 md:hidden" data-testid="recent-attendance-mobile-list">
            {records.map((r) => (
              <div key={r.id} className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 font-display font-extrabold text-blue-950">{r.student_name}</div>
                  <span className={`shrink-0 rounded-full px-2.5 py-0.5 text-[11px] font-bold ${r.status === "present" ? "bg-green-500/10 text-green-600" : r.status === "late" ? "bg-yellow-500/15 text-yellow-600" : "bg-red-500/10 text-red-600"}`}>{r.status}</span>
                </div>
                <div className="num mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                  <span>{r.method}</span>
                  <span>·</span>
                  <span>{new Date(r.created_at).toLocaleString()}</span>
                </div>
              </div>
            ))}
            {records.length === 0 && <div className="p-6 text-center text-sm text-muted-foreground">{t("noRecordsYet")}</div>}
          </div>
          <div className="hidden md:block">
          <Table>
            <TableHeader><TableRow className="bg-blue-950 hover:bg-blue-950">
              <TableHead className="text-xs font-extrabold uppercase tracking-widest text-yellow-400">Student</TableHead><TableHead className="text-xs font-extrabold uppercase tracking-widest text-yellow-400">Method</TableHead><TableHead className="text-xs font-extrabold uppercase tracking-widest text-yellow-400">Status</TableHead><TableHead className="text-xs font-extrabold uppercase tracking-widest text-yellow-400">When</TableHead>
            </TableRow></TableHeader>
            <TableBody>{records.map((r) => (
              <TableRow key={r.id} className="transition-colors hover:bg-orange-50">
                <TableCell className="font-semibold text-blue-950">{r.student_name}</TableCell>
                <TableCell className="num">{r.method}</TableCell>
                <TableCell><span className={`rounded-full px-2.5 py-0.5 text-xs font-bold ${r.status === "present" ? "bg-green-500/10 text-green-600" : r.status === "late" ? "bg-yellow-500/15 text-yellow-600" : "bg-red-500/10 text-red-600"}`}>{r.status}</span></TableCell>
                <TableCell className="num text-xs text-muted-foreground">{new Date(r.created_at).toLocaleString()}</TableCell>
              </TableRow>
            ))}{records.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground">{t("noRecordsYet")}</TableCell></TableRow>}</TableBody>
          </Table>
          </div>
        </CardContent></Card>
      </section>
    </div>
  );
}
